import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { useCurrency } from '../../context/CurrencyContext';
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';
import SubmitForApprovalButton from '../approval/SubmitForApprovalButton';
import WithdrawButton from '../approval/WithdrawButton';
import { CONTENT_TYPES } from '../../constants/contentTypes';

const getItemImage = (item) => {
  if (!item) return null;
  if (item.merchandise?.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.merchandise.thumbnail.linkLocalizations[0].mediaLink;
  if (item.ticket?.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.ticket.thumbnail.linkLocalizations[0].mediaLink;
  if (item.badge?.media?.linkLocalizations?.[0]?.mediaLink)
    return item.badge.media.linkLocalizations[0].mediaLink;
  if (item.avatar?.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.avatar.thumbnail.linkLocalizations[0].mediaLink;
  // Package — thumbnail lives directly on the object, not nested under a type sub-object.
  if (item.thumbnail?.linkLocalizations?.[0]?.mediaLink)
    return item.thumbnail.linkLocalizations[0].mediaLink;
  return null;
};

const getItemName = (item, fallbackId, isPackage) =>
  item?.nameLocalizations?.values?.[0]?.value ?? item?.name ?? `${isPackage ? 'Package' : 'Item'} ${fallbackId}`;

/**
 * A card for an item already on the free trial eligibility list — same visual base as
 * ItemCard/FreeTrialItemCard, but the footer carries the full approval lifecycle
 * (badge, View proposal, Withdraw/Dismiss, Submit for Approval) instead of an Add button,
 * mirroring exactly what the Discount Scheduler table row did, just as a card.
 */
const FreeTrialListedItemCard = ({
  row, item, itemTypes = [], canEdit = true,
  onViewProposal, onWithdrawn, onSubmitted, onRemove,
}) => {
  const { activeCurrency, currencies } = useCurrency();
  const itemCurrencyId   = item?.prices?.[0]?.currencyId ?? null;
  const resolvedCurrency = (itemCurrencyId ? currencies.find(c => c.id === itemCurrencyId) : null) ?? activeCurrency;
  const decimalPlaces    = resolvedCurrency?.decimalPlaces ?? 2;
  const factor           = Math.pow(10, decimalPlaces);
  const symbol           = resolvedCurrency?.symbol ?? '';
  const baseCostMinor    = item?.prices?.[0]?.baseCost ?? item?.baseCost ?? 0;
  const baseCost         = baseCostMinor / factor;

  const imageUrl = getItemImage(item);
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [imageUrl]);

  const isPackage = row.packageId != null;
  const typeLabel = isPackage ? 'Package' : (itemTypes.find(t => t.id === item?.itemTypeId)?.Type ?? '—');
  const itemName  = getItemName(item, isPackage ? row.packageId : row.itemId, isPackage);

  const isSnapshot    = row._viewType === 'snapshot';
  const isLocked      = row.approvalStatus === 'pending' || row.approvalStatus === 'rejected';
  const canDirectEdit = row.contentStatus === 'draft' && !isLocked;
  const viewApprovalRecord = row.approvalId ? {
    approvalId:      row.approvalId,
    actionType:      row.approvalActionType,
    contentType:     CONTENT_TYPES.FREE_TRIAL_ITEM,
    sectionLabel:    'Free Trial Item',
    contentName:     itemName,
    submittedByName: '',
    submittedAt:     null,
    status:          row.approvalStatus,
  } : null;

  return (
    <Box
      sx={{
        bgcolor: 'white', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <Box sx={{ position: 'relative', height: 140, bgcolor: '#e2e8f0', overflow: 'hidden', flexShrink: 0 }}>
        {imageUrl && !imgError ? (
          <Box component="img" src={imageUrl} alt={itemName} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setImgError(true)} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <ShoppingBagIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
          </Box>
        )}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.45)', p: 1 }}>
          <Box sx={{ display: 'inline-block', px: 1, py: 0.3, borderRadius: '6px', bgcolor: 'rgba(59,130,246,0.8)', fontSize: 9, fontWeight: 700, color: 'white', lineHeight: 1.4 }}>
            {typeLabel}
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography fontWeight={700} fontSize={13} sx={{ color: '#1e293b', lineHeight: 1.35 }}>
          {itemName}
        </Typography>
        <Typography fontWeight={800} fontSize={16} sx={{ color: '#1e3a5f' }}>
          {symbol}{baseCost.toFixed(decimalPlaces)}
        </Typography>
      </Box>

      <Box sx={{ px: 1.75, py: 1.25, borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
        {(isSnapshot ? row.approvalStatus : row.contentStatus) && (
          <ApprovalStatusBadge status={isSnapshot ? row.approvalStatus : row.contentStatus} actionType={row.approvalActionType} />
        )}

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', width: '100%' }}>
          {isSnapshot && viewApprovalRecord && (
            <Button
              variant="outlined" size="small"
              startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
              onClick={() => onViewProposal(viewApprovalRecord, row._liveId ?? row.id)}
              sx={{
                borderColor: '#3b82f6', color: '#1d4ed8', bgcolor: '#eff6ff', borderRadius: '8px',
                px: 1.25, py: 0.4, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.3, boxShadow: 'none', '&:hover': { bgcolor: '#dbeafe' },
              }}
            >
              View
            </Button>
          )}
          {canEdit && isSnapshot && row.approvalStatus === 'pending' && row.approvalId && (
            <WithdrawButton approvalId={row.approvalId} onWithdrawn={() => onWithdrawn(row)} />
          )}
          {canEdit && isSnapshot && row.approvalStatus === 'rejected' && row.approvalId && (
            <WithdrawButton approvalId={row.approvalId} onWithdrawn={() => onWithdrawn(row)} variant="dismiss" />
          )}
          {canEdit && !isSnapshot && row.approvalStatus === 'pending' && row.approvalId && (
            <WithdrawButton approvalId={row.approvalId} onWithdrawn={() => onWithdrawn(row)} />
          )}
          {canEdit && !isSnapshot && canDirectEdit && (
            <SubmitForApprovalButton
              contentType={CONTENT_TYPES.FREE_TRIAL_ITEM}
              contentId={row.id}
              contentName={itemName}
              onSubmitted={() => onSubmitted(row)}
            />
          )}
          {!isSnapshot && canEdit && (
            <Tooltip title={isLocked ? 'Withdraw pending changes to remove' : 'Remove from free trial list'}>
              <span style={{ marginLeft: 'auto' }}>
                <IconButton
                  size="small"
                  disabled={isLocked}
                  onClick={() => onRemove(row)}
                  sx={{
                    bgcolor: '#fff1f2', color: '#ef4444', borderRadius: '8px', border: '1px solid #fecdd3',
                    '&:hover': { bgcolor: '#fee2e2', color: '#dc2626', borderColor: '#fca5a5' },
                    '&.Mui-disabled': { opacity: 0.45 },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FreeTrialListedItemCard;
